import React from 'react';
import { FERMENTATION_PROCESSES } from '../../types/baking';

interface BreadParametersFormProps {
  parameters: {
    fermentationType: 'direct' | 'cold-bulk' | 'slow-room' | 'pre-proofed';
    fermentationDuration: number;
    bakingMethod: 'dutch-oven' | 'direct' | 'wood-fired';
    temperature: number;
    weight: number;
    bakingDuration: number;
    hydration: number;
  };
  onChange: (parameters: BreadParametersFormProps['parameters']) => void;
}

export function BreadParametersForm({ parameters, onChange }: BreadParametersFormProps) {
  const handleChange = (name: string, value: any) => {
    // Ensure numeric values are valid numbers
    if (typeof value === 'string' && value.trim() === '') {
      value = 0;
    }
    
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      onChange({
        ...parameters,
        [name]: numValue
      });
    } else {
      onChange({
        ...parameters,
        [name]: value
      });
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="font-medium text-white">Baking Parameters</h3>
      <p className="text-sm text-white/80 mb-6 mt-2">
        Tell us exactly how you made your bread. This will help us give you a more precise and personalized feedback.
      </p>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-white/90 mb-2">
            Fermentation Process
          </label>
          <select
            value={parameters.fermentationType}
            onChange={(e) => handleChange('fermentationType', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20 appearance-none cursor-pointer"
          >
            {FERMENTATION_PROCESSES.map(process => (
              <option 
                key={process.id} 
                value={process.id} 
                className="bg-[#1C1C1C] text-white py-2 hover:bg-[#2C2C2C]"
              >
                {process.name}
              </option>
            ))}
          </select>
          <div className="mt-2 text-sm text-white/70">
            {FERMENTATION_PROCESSES.find(p => p.id === parameters.fermentationType)?.description}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-white/90 mb-2">
            Fermentation Duration (hours)
          </label>
          <input
            type="number"
            min="1"
            max="72"
            value={parameters.fermentationDuration || ''}
            onChange={(e) => handleChange('fermentationDuration', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white/90 mb-2">
            Baking Method
          </label>
          <select
            value={parameters.bakingMethod}
            onChange={(e) => handleChange('bakingMethod', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20 appearance-none cursor-pointer"
          >
            <option value="dutch-oven" className="bg-[#1C1C1C] text-white py-2">Dutch Oven</option>
            <option value="direct" className="bg-[#1C1C1C] text-white py-2">Direct Oven Baking</option>
            <option value="wood-fired" className="bg-[#1C1C1C] text-white py-2">Wood-Fired Oven</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-white/90 mb-2">
            Dough Hydration ({parameters.hydration}%)
          </label>
          <input
            type="range"
            min="50"
            max="100"
            step="1"
            value={parameters.hydration || 65}
            onChange={(e) => handleChange('hydration', e.target.value)}
            className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400 [&::-webkit-slider-thumb]:cursor-pointer"
          />
          <div className="flex justify-between text-xs text-white/50 mt-1">
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-white/90 mb-2">
            Temperature ({parameters.temperature}°C)
          </label>
          <input
            type="range"
            min="200"
            max="300"
            value={parameters.temperature || 200}
            onChange={(e) => handleChange('temperature', e.target.value)}
            className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400 [&::-webkit-slider-thumb]:cursor-pointer"
          />
          <div className="flex justify-between text-xs text-white/50 mt-1">
            <span>200°C</span>
            <span>300°C</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-white/90 mb-2">
            Bread Weight ({parameters.weight}kg)
          </label>
          <input
            type="range"
            min="0"
            max="3"
            step="0.1"
            value={parameters.weight || 0}
            onChange={(e) => handleChange('weight', e.target.value)}
            className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400 [&::-webkit-slider-thumb]:cursor-pointer"
          />
          <div className="flex justify-between text-xs text-white/50 mt-1">
            <span>0kg</span>
            <span>3kg</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-white/90 mb-2">
            Baking Duration ({parameters.bakingDuration} minutes)
          </label>
          <input
            type="range"
            min="20"
            max="120"
            value={parameters.bakingDuration || 20}
            onChange={(e) => handleChange('bakingDuration', e.target.value)}
            className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400 [&::-webkit-slider-thumb]:cursor-pointer"
          />
          <div className="flex justify-between text-xs text-white/50 mt-1">
            <span>20min</span>
            <span>2hrs</span>
          </div>
        </div>
      </div>
    </div>
  );
}